import api from './api';

export interface PlatformStatus {
  stats: { totalOrgs: number; totalUsers: number; activeOrgs: number };
  recentOrgs: any[];
  recentUsers: any[];
  integrations: { youtubeCookies: 'configured' | 'missing' | 'error' };
}

export interface YoutubeCookiesStatus {
  status: 'configured' | 'missing' | 'error';
  secretName: string;
  lastChangedDate?: string;
  createdDate?: string;
}

export type FeatureFlagKey = 'video-editor' | 'video-understanding' | 'avatar-creation';

export interface FeatureFlag {
  _id: string;
  key: FeatureFlagKey;
  label: string;
  description?: string;
  enabled: boolean;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PlanEntitlements {
  videoProcessingHoursPerMonth: number;
  videoTranslationsPerMonth: number;
  audioTranslationsPerMonth: number;
  speechToTextMinutesPerMonth: number;
  textToSpeechCharactersPerMonth: number;
  clipsPerMonth: number;
  connectedAccountsLimit: number;
  translationLanguagesTier?: '50+' | '100+';
  voiceCloning: boolean;
  lipSync: boolean;
  thumbnailGenerator: boolean;
  schedulingCalendar: boolean;
  analyticsDashboard: boolean;
  clientWorkspaces: number;
  approvalWorkflows: boolean;
  brandKits: boolean;
  prioritySupport: boolean;
  apiAccess: boolean;
  ssoSaml: boolean;
  customAiModels: boolean;
  maxTeamMembers: number;
  maxVoices: number;
  maxAvatars: number;
  maxProjects: number;
}

export interface AdminPlan {
  _id: string;
  key: string;
  name: string;
  description?: string;
  storageQuotaBytes: number;
  retentionDaysDefault: number;
  retentionDaysMax: number;
  monthlyPriceUsd?: number;
  yearlyPriceUsd?: number;
  isCustomPricing: boolean;
  isTrial: boolean;
  trialDays?: number;
  mostPopular: boolean;
  offeredOnPricingPage: boolean;
  entitlements: PlanEntitlements;
  dodoProductIdMonthly?: string;
  dodoProductIdYearly?: string;
  isActive: boolean;
  orgCount: number;
  createdAt: string;
  updatedAt: string;
}

export type AdminPlanInput = Omit<AdminPlan, '_id' | 'orgCount' | 'createdAt' | 'updatedAt'>;

export type AIModelSlot = 'elevenlabs_tts';

export interface AIModelOption {
  id: string;
  label: string;
  description: string;
  languages: string;
  latency: string;
}

export interface AIModelOrgOverride {
  organizationId: string;
  organizationName: string;
  modelId: string;
}

export interface AIModelSlotInfo {
  slot: AIModelSlot;
  label: string;
  description: string;
  options: AIModelOption[];
  platformDefaultModelId: string;
  orgOverrides: AIModelOrgOverride[];
}

const platformAdminApi = {
  getStatus: () =>
    api.get<{ success: boolean; data: PlatformStatus }>('/admin/platform/status'),

  getOrganizations: () =>
    api.get<{ success: boolean; data: any[] }>('/admin/platform/organizations'),

  getUsers: () =>
    api.get<{ success: boolean; data: any[] }>('/admin/platform/users'),

  getYoutubeCookiesStatus: () =>
    api.get<{ success: boolean; data: YoutubeCookiesStatus }>('/admin/platform/settings/youtube-cookies/status'),

  updateYoutubeCookies: (cookiesContent: string) =>
    api.put<{ success: boolean; message: string; data: any }>('/admin/platform/settings/youtube-cookies', { cookiesContent }),

  setUserRole: (userId: string, role: string) =>
    api.put<{ success: boolean; data: any }>(`/admin/platform/users/${userId}/role`, { role }),

  getFeatureFlags: () =>
    api.get<{ success: boolean; data: FeatureFlag[] }>('/admin/platform/feature-flags'),

  updateFeatureFlag: (key: FeatureFlagKey, enabled: boolean) =>
    api.patch<{ success: boolean; data: FeatureFlag }>(`/admin/platform/feature-flags/${key}`, { enabled }),

  // Per-org opt-in add-ons (e.g. 'newsroom') — independent of the platform-wide
  // flags above and of the org's plan; defaults to disabled per organization.
  getOrgFeature: (organizationId: string, feature: string) =>
    api.get<{ success: boolean; data: { enabled: boolean; enabledAt?: string; notes?: string } }>(
      `/admin/platform/organizations/${organizationId}/features/${feature}`
    ),

  updateOrgFeature: (organizationId: string, feature: string, enabled: boolean, notes?: string) =>
    api.put<{ success: boolean; data: any }>(
      `/admin/platform/organizations/${organizationId}/features/${feature}`,
      { enabled, notes }
    ),

  getPlans: () =>
    api.get<{ success: boolean; data: AdminPlan[] }>('/admin/platform/plans'),

  createPlan: (data: AdminPlanInput) =>
    api.post<{ success: boolean; data: AdminPlan }>('/admin/platform/plans', data),

  updatePlan: (key: string, data: Partial<AdminPlanInput>) =>
    api.put<{ success: boolean; data: AdminPlan }>(`/admin/platform/plans/${key}`, data),

  setPlanActive: (key: string, isActive: boolean) =>
    api.patch<{ success: boolean; data: AdminPlan }>(`/admin/platform/plans/${key}/active`, { isActive }),

  deletePlan: (key: string) =>
    api.delete<{ success: boolean; data: { key: string } }>(`/admin/platform/plans/${key}`),

  setOrganizationPlan: (organizationId: string, plan: string) =>
    api.put<{ success: boolean; data: any }>(`/admin/platform/organizations/${organizationId}/plan`, { plan }),

  getAIModels: () =>
    api.get<{ success: boolean; data: AIModelSlotInfo[] }>('/admin/platform/ai-models'),

  setAIModelPlatformDefault: (slot: AIModelSlot, modelId: string) =>
    api.put<{ success: boolean; data: { slot: AIModelSlot; platformDefaultModelId: string } }>(
      `/admin/platform/ai-models/${slot}/default`,
      { modelId }
    ),

  // modelId: null clears the override for these orgs, reverting them to the platform default
  setAIModelOrgOverrides: (slot: AIModelSlot, organizationIds: string[], modelId: string | null) =>
    api.put<{ success: boolean; data: any }>(
      `/admin/platform/ai-models/${slot}/organizations`,
      { organizationIds, modelId }
    ),
};

export default platformAdminApi;
